import { z } from 'zod'
import { TokenSchema } from '../schemas'

export type RemoveOnlyKeys =
  | 'cf-docusign-oauth-code'
  | 'cf-service-oauth-code'
  | 'cf-dms-oauth-code'
  | 'cf-adobe-oauth-code'

const ActivityDateRangeFilterSchema = z.object({
  selectedQuickRange: z.enum([
    'off',
    'allTime',
    'today',
    'yesterday',
    'lastFullWeek',
    'lastFullMonth',
    'exactDate',
    'beforeEnd',
    'afterStart',
    'custom',
  ]),
})
export type ActivityDateRangeFilter = z.infer<typeof ActivityDateRangeFilterSchema>

const SavedExportOrSendSelectionsSchema = z.object({
  versionsSelected: z.boolean(),
  selectedVersionType: z.enum([
    'latest_versions',
    'closing_versions',
    'latest_uncompiled_versions',
    'all_versions',
  ]),
  renderStateValue: z.enum(['rendered', 'unrendered', 'both']),
  optionsSelected: z.array(z.enum(['executed_signature_pages', 'schedules', 'markups'])),
  blacklinesSelected: z.boolean(),
  blacklineType: z.enum(['latest_blackline', 'all_blacklines']),
  putInSeparateFoldersSelected: z.boolean(),
})

const LocalStorageSchema = {
  clofoToken: TokenSchema,
  sort: z.object({
    column: z.enum([
      'title',
      'client_number',
      'matter_number',
      'closing_date',
      'user_last_accessed',
      'deal_status_id',
      'has_external_users',
      'owner_organization',
      'created_by_name',
    ]),
    direction: z.enum(['asc', 'desc']),
  }),
  dealTableView: z.enum(['list', 'categories']),
  categorySort: z.enum(['asc', 'desc']),
  usersActivityFilter: ActivityDateRangeFilterSchema,
  dealsActivityFilter: ActivityDateRangeFilterSchema,
  ignoreFinalizedItemsWarning: z.string().optional(),
  ignoreBulkFinalizeWarning: z.string().optional(),
  ignoreTemplateWarningExcel: z.string().optional(),
  ignoreTemplateWarningSasd: z.string().optional(),
  selectedClosingBookObjectsByDeal: z.record(z.string(), z.array(z.number())),
  /**
   * Thumbnail zoom on the manage-signatures editor, 1-8. Deliberately the same key the Ember
   * editor uses so a user's zoom carries across the two implementations — Ember writes a bare
   * integer, which round-trips through `JSON.stringify`/`parse` unchanged.
   */
  zoomLevel: z.number().int().min(1).max(8),
  /** Same key the Ember login uses, so a dismissal in either app hides the notice in both. */
  'cf-cim-notice-dismissed': z.boolean(),
  'exportOrSendModalSelections-export': SavedExportOrSendSelectionsSchema,
  'exportOrSendModalSelections-send': SavedExportOrSendSelectionsSchema,
} as const

export type LocalStorageKey = keyof typeof LocalStorageSchema
export type LocalStorageValueType<K extends LocalStorageKey> = z.infer<
  (typeof LocalStorageSchema)[K]
>

function getItem<K extends LocalStorageKey>(key: K): LocalStorageValueType<K> | undefined
function getItem(key: LocalStorageKey): unknown {
  const item = localStorage.getItem(key)
  const schema = LocalStorageSchema[key]
  let itemInJson: unknown = null
  try {
    itemInJson = item && JSON.parse(item)
  } catch (e) {
    if (!(e instanceof SyntaxError)) {
      throw e
    }
    console.error('Corrupt localStorage item:', key, item)
    localStorage.removeItem(key)
    return
  }
  const { success, data } = schema.safeParse(itemInJson)
  if (success) {
    return data
  }
}

const setItem = <K extends LocalStorageKey>(key: K, item: LocalStorageValueType<K>) => {
  localStorage.setItem(key, JSON.stringify(item))
}

const removeItem = (key: LocalStorageKey | RemoveOnlyKeys) => {
  localStorage.removeItem(key)
}

export const ls = {
  getItem,
  setItem,
  removeItem,
}
